import ApplicationController from "./application_controller"

// Misspelled search-result popup behavior:
// - hover indicator => open popup
// - move indicator -> popup => keep open (with a small forgiveness delay for first-row "below" positioning)
// - leave popup => close
//
// This controller is mounted per misspelled wrapper element rendered by the helper.

export default class extends ApplicationController {
  static targets = ['misspelledIndicator', 'misspelledPopupCard']
  static classes = ['popupVisible', 'popupPositionBelow']
  static values = {
    // NOTE: This codebase uses an older Stimulus version that does not support
    // the object-style value definition ({ type, default }). Keep this in the
    // legacy Stimulus format for compatibility.
    firstRowCloseDelayMs: Number
  }

  connect() {
    this.closeTimeoutId = null
    this._tableResponsiveEl = null
    if (!this.hasMisspelledIndicatorTarget || !this.hasMisspelledPopupCardTarget) return

    const isFirstRowItem = this.detectFirstRow(this.element)
    this.updatePopupPosition(isFirstRowItem)
    this.expandContainerForSingleRowPopup(this.element)

    this._onIndicatorEnter = () => this.handleIndicatorEnter()
    this._onIndicatorLeave = () => this.handleIndicatorLeave()
    this._onPopupEnter = () => this.handlePopupEnter()
    this._onPopupLeave = () => this.handlePopupLeave()

    this.misspelledIndicatorTarget.addEventListener('mouseenter', this._onIndicatorEnter)
    this.misspelledIndicatorTarget.addEventListener('mouseleave', this._onIndicatorLeave)
    this.misspelledPopupCardTarget.addEventListener('mouseenter', this._onPopupEnter)
    this.misspelledPopupCardTarget.addEventListener('mouseleave', this._onPopupLeave)
  }

  disconnect() {
    this.clearCloseTimeout()
    this.removeSingleRowRoomClass()

    if (this.hasMisspelledIndicatorTarget && this._onIndicatorEnter) {
      this.misspelledIndicatorTarget.removeEventListener('mouseenter', this._onIndicatorEnter)
    }
    if (this.hasMisspelledIndicatorTarget && this._onIndicatorLeave) {
      this.misspelledIndicatorTarget.removeEventListener('mouseleave', this._onIndicatorLeave)
    }
    if (this.hasMisspelledPopupCardTarget && this._onPopupEnter) {
      this.misspelledPopupCardTarget.removeEventListener('mouseenter', this._onPopupEnter)
    }
    if (this.hasMisspelledPopupCardTarget && this._onPopupLeave) {
      this.misspelledPopupCardTarget.removeEventListener('mouseleave', this._onPopupLeave)
    }
  }

  // Misspelled indicator button hover
  handleIndicatorEnter() {
    this.clearCloseTimeout()
    this.openPopup()
  }

  handleIndicatorLeave() {
    if (this.isPopupPositionedBelow()) {
      this.scheduleClose()
    } else {
      this.closePopup()
    }
  }

  // Popup hover
  handlePopupEnter() {
    this.clearCloseTimeout()
    this.openPopup()
  }

  handlePopupLeave() {
    this.closePopup()
  }

  preventIndicatorWordSearch(event) {
    event.stopPropagation()
  }

  openContextModalFromPopup(event) {
    event.stopPropagation()
    event.preventDefault()

    // Open the same context as a left-side row click would open.
    // We intentionally do not rely on an extra hidden button inside the misspelled UI.
    const row = event.currentTarget.closest('tr')
    if (!row) return

    const contextBtn = row.querySelector('.js-search-output-result-context-view-btn')
    if (contextBtn) contextBtn.click()
  }

  // Internal helpers
  openPopup() {
    this.misspelledPopupCardTarget.classList.add(this.popupVisibleClass)
  }

  closePopup() {
    this.misspelledPopupCardTarget.classList.remove(this.popupVisibleClass)
  }

  scheduleClose() {
    this.clearCloseTimeout()
    this.closeTimeoutId = setTimeout(() => {
      this.closePopup()
      this.closeTimeoutId = null
    }, this.firstRowCloseDelayMs())
  }

  clearCloseTimeout() {
    if (!this.closeTimeoutId) return
    clearTimeout(this.closeTimeoutId)
    this.closeTimeoutId = null
  }

  firstRowCloseDelayMs() {
    if (this.hasFirstRowCloseDelayMsValue) return this.firstRowCloseDelayMsValue
    return 175
  }

  detectFirstRow(wrapperElement) {
    const tableRow = wrapperElement.closest('tr')
    if (!tableRow) return false

    const tableBody = tableRow.parentElement
    if (!tableBody || tableBody.tagName !== 'TBODY') return false

    return tableBody.firstElementChild === tableRow
  }

  updatePopupPosition(isFirstRowItem) {
    if (isFirstRowItem) {
      this.misspelledPopupCardTarget.classList.add(this.popupPositionBelowClass)
    } else {
      this.misspelledPopupCardTarget.classList.remove(this.popupPositionBelowClass)
    }
  }

  // Adds bottom padding to the scroll container when there's only one result row, preventing the popup from being clipped.
  expandContainerForSingleRowPopup(wrapperElement) {
    const tableRow = wrapperElement.closest('tr')
    if (!tableRow) return

    const tableBody = tableRow.parentElement
    if (!tableBody || tableBody.tagName !== 'TBODY') return

    const rowCount = tableBody.children.length
    if (rowCount !== 1) return

    this._tableResponsiveEl = wrapperElement.closest('.table-responsive')
    if (!this._tableResponsiveEl) return
    this._tableResponsiveEl.classList.add('misspelled-single-row-room')
  }

  removeSingleRowRoomClass() {
    if (!this._tableResponsiveEl) return
    this._tableResponsiveEl.classList.remove('misspelled-single-row-room')
    this._tableResponsiveEl = null
  }

  isPopupPositionedBelow() {
    return this.misspelledPopupCardTarget.classList.contains(this.popupPositionBelowClass)
  }
}
